/**
 * Paramétrage article: one article's place in the tree, its brand, its web
 * name, its attribute group and the values of that group's attributes.
 *
 * Master and detail on one screen, because the job is a run through a list:
 * pick the next article, place it, fill in its characteristics, move on.
 *
 * **The tree is chosen one level at a time.** One select per configured level
 * — Activité, then Famille under it, then Sous-famille, then Segment — so the
 * choices at each step are only the ones that exist under the previous
 * answer. A single select listing four hundred indented nodes is a select
 * nobody scrolls.
 *
 * **What is inherited says so.** An article with no category of its own shows
 * its family's, and an article with no group of its own shows the group its
 * category gives it, with the node it comes from. Saving without touching
 * either keeps them inherited; the reset button returns everything to that
 * state.
 *
 * **The fields follow the tree's own order** — Activité, Famille,
 * Sous-famille, Segment, then Marque, Image web URL and Groupe d'attributs —
 * and every level is drawn, disabled until the one above is answered.
 *
 * **The attribute inputs follow the group in the select.** Choosing a group
 * draws its fields at once, and one save writes the fiche then the values;
 * values typed under another group stay on the article and reappear if the
 * group comes back.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import { ApiError, type ApiClient } from "@hardauto/core-api";
import { useAction, useDebounced, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { Badge, Button, Card, Checkbox, Input, Page, Select, useToast } from "@hardauto/core-ui";

import { CatalogApi, type AttributeDefinition, type CategoryNode, type FicheRow } from "../api";
import { NAMESPACE } from "../i18n";
import "./catalog.css";

/** The chain of nodes from a root down to ``id``, or empty when it is not in the tree. */
function chainTo(nodes: CategoryNode[], id: string | null): CategoryNode[] {
  if (!id) return [];
  for (const node of nodes) {
    if (node.category.id === id) return [node];
    const below = chainTo(node.children, id);
    if (below.length > 0) return [node, ...below];
  }
  return [];
}

function reason(error: unknown, fallback: string): string {
  return error instanceof ApiError && error.message ? error.message : fallback;
}

export function ArticleSetupScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new CatalogApi(client), [client]);

  const [search, setSearch] = useState("");
  const settled = useDebounced(search, 250);
  const [selectedId, setSelectedId] = useState("");

  const listFetcher = useCallback(
    () => api.listFiches({ search: settled.trim() || undefined, limit: 50 }),
    [api, settled],
  );
  const list = useResource(listFetcher);
  const rows: FicheRow[] = list.data?.items ?? [];
  const current = selectedId || rows[0]?.article_id || "";

  return (
    <Page title={t("articleSetup.title")} description={t("articleSetup.description")}>
      <div className="hardauto-catalog__split">
        <Card title={t("articleSetup.list")} compactTitle>
          <div className="hardauto-stack">
            <Field label={t("articleSetup.search")}>
              {(props) => (
                <Input
                  {...props}
                  type="search"
                  value={search}
                  placeholder={t("articleSetup.searchPlaceholder")}
                  onChange={(event) => setSearch(event.target.value)}
                />
              )}
            </Field>
            {list.loading ? (
              <p className="hardauto-cell__muted">{t("articleSetup.loading")}</p>
            ) : rows.length === 0 ? (
              <p className="hardauto-cell__muted">{t("articleSetup.noArticle")}</p>
            ) : (
              <ul className="hardauto-picklist" aria-label={t("articleSetup.list")}>
                {rows.map((row) => (
                  <li key={row.article_id}>
                    <button
                      type="button"
                      className="hardauto-picklist__item"
                      aria-current={row.article_id === current ? "true" : undefined}
                      onClick={() => setSelectedId(row.article_id)}
                    >
                      <span className="hardauto-picklist__title">{row.reference}</span>
                      <span className="hardauto-cell__muted">
                        {row.web_designation || row.designation}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {list.data && list.data.total > rows.length ? (
              <p className="hardauto-cell__muted">
                {t("articleSetup.more", { shown: rows.length, total: list.data.total })}
              </p>
            ) : null}
          </div>
        </Card>

        {current ? (
          <ArticleDetail key={current} api={api} articleId={current} onSaved={list.reload} />
        ) : (
          <Card>
            <p className="hardauto-cell__muted">{t("articleSetup.pick")}</p>
          </Card>
        )}
      </div>
    </Page>
  );
}

function ArticleDetail({
  api,
  articleId,
  onSaved,
}: {
  api: CatalogApi;
  articleId: string;
  onSaved: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const mayWrite = useCan("catalog.enrichment.write");

  const ficheFetcher = useCallback(() => api.fiche(articleId), [api, articleId]);
  const fiche = useResource(ficheFetcher);
  const treeFetcher = useCallback(() => api.categoryTree(false), [api]);
  const tree = useResource(treeFetcher);
  const levelsFetcher = useCallback(() => api.levels(), [api]);
  const levels = useResource(levelsFetcher);
  const groupsFetcher = useCallback(() => api.attributeGroups(false), [api]);
  const groups = useResource(groupsFetcher);
  const brandsFetcher = useCallback(() => api.brands(false), [api]);
  const brands = useResource(brandsFetcher);

  // Memoised on the response, not rebuilt per render: the effect below seeds
  // the form from it, and a fresh `[]` each render would reseed for ever.
  const roots = useMemo(() => tree.data?.roots ?? [], [tree.data]);
  const data = fiche.data;

  // The form's own state, seeded from the fiche each time it (re)loads.
  const [chain, setChain] = useState<string[]>([]);
  const [brand, setBrand] = useState("");
  const [designation, setDesignation] = useState("");
  const [image, setImage] = useState("");
  const [groupId, setGroupId] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [confirmReset, setConfirmReset] = useState(false);

  // Two effects, not one: the tree may arrive after the fiche, and placing the
  // selects when it does must not wipe what somebody has started typing.
  useEffect(() => {
    if (!data) return;
    setBrand(data.web_brand);
    setDesignation(data.web_designation);
    setImage(data.web_image_url ?? "");
    setGroupId(data.own_attribute_group_id ?? "");
    setValues(Object.fromEntries(data.facts.attributes.map((item) => [item.key, item.value])));
  }, [data]);

  useEffect(() => {
    if (!data) return;
    setChain(
      data.category_inherited
        ? []
        : chainTo(roots, data.category_id).map((node) => node.category.id),
    );
  }, [data, roots]);

  if (fiche.loading || !data) {
    return (
      <Card>
        <p className="hardauto-cell__muted">{t("articleSetup.loading")}</p>
      </Card>
    );
  }

  // Always the four levels of the tree, in order — Activité, Famille,
  // Sous-famille, Segment — whether or not the level above has an answer: a
  // select that appears only once its parent is chosen is a field nobody
  // knows is there. A level with nothing to choose is drawn, and disabled.
  const depthCount = 4;
  const levelLabel = (depth: number) =>
    levels.data?.items.find((item) => item.depth === depth)?.label ??
    t("articleSetup.level", { depth });

  // The options at each depth: the roots, then the children of the answer above.
  const selects: { depth: number; options: CategoryNode[]; value: string }[] = [];
  let siblings: CategoryNode[] = roots;
  for (let depth = 1; depth <= depthCount; depth++) {
    const value = chain[depth - 1] ?? "";
    selects.push({ depth, options: siblings, value });
    const chosen = siblings.find((node) => node.category.id === value);
    siblings = chosen ? chosen.children : [];
  }

  const inheritedGroup = data.own_attribute_group_id ? null : data.attribute_group;
  const brandOptions = (brands.data?.items ?? []).map((item) => item.label);
  if (brand && !brandOptions.includes(brand)) brandOptions.unshift(brand);

  // The group whose fields are drawn is the one in the select, as soon as it
  // is chosen — not the one last saved. Choosing a group is how somebody asks
  // which characteristics to fill in, and the answer cannot wait for a save.
  const chosenGroup = groupId
    ? (groups.data?.items ?? []).find((group) => group.id === groupId)
    : inheritedGroup;
  const definitions: AttributeDefinition[] = chosenGroup?.attributes ?? [];
  const imageShown = /^https?:\/\//i.test(image.trim());

  function save(task: () => Promise<unknown>, done: string) {
    void action.run("save", async () => {
      try {
        await task();
        toast.show(done, { tone: "success" });
        fiche.reload();
        onSaved();
      } catch (error) {
        toast.show(reason(error, t("error.title")), { tone: "danger" });
      }
    });
  }

  const leaf = chain[chain.length - 1];

  return (
    <Card
      title={data.facts.reference}
      description={data.facts.designation}
      actions={
        mayWrite ? (
          confirmReset ? (
            <span className="hardauto-inline-actions">
              <Button
                size="sm"
                variant="danger"
                busy={action.isBusy("save")}
                onClick={() => {
                  setConfirmReset(false);
                  save(
                    () =>
                      api.saveFiche(articleId, {
                        clear_category: true,
                        clear_attribute_group: true,
                        web_brand: "",
                        web_designation: "",
                        web_image_url: "",
                      }),
                    t("articleSetup.resetDone"),
                  );
                }}
              >
                {t("articleSetup.resetConfirm")}
              </Button>
              <Button size="sm" onClick={() => setConfirmReset(false)}>
                {t("categories.cancel")}
              </Button>
            </span>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirmReset(true)}>
              {t("articleSetup.reset")}
            </Button>
          )
        ) : undefined
      }
    >
      <form
        className="hardauto-setup-grid"
        onSubmit={(event) => {
          event.preventDefault();
          save(async () => {
            // The fiche first: the values are checked against the group the
            // article has, and the group may be the one just chosen.
            await api.saveFiche(articleId, {
              ...(leaf ? { category_id: leaf } : { clear_category: true }),
              ...(groupId ? { attribute_group_id: groupId } : { clear_attribute_group: true }),
              web_brand: brand,
              web_image_url: image.trim(),
              web_designation: designation,
            });
            if (definitions.length > 0) {
              await api.setAttributes(
                articleId,
                Object.fromEntries(definitions.map((item) => [item.key, values[item.key] ?? ""])),
              );
            }
          }, t("articleSetup.saved"));
        }}
      >
        {data.category_inherited && chain.length === 0 ? (
          <p className="hardauto-setup-grid__wide hardauto-cell__muted">
            {t("articleSetup.categoryInherited", {
              path: data.category_path,
              family: data.facts.family_code || "—",
            })}
          </p>
        ) : null}

        {selects.map(({ depth, options, value }) => (
          <Field key={depth} label={levelLabel(depth)}>
            {(props) => (
              <Select
                {...props}
                value={value}
                disabled={!mayWrite || options.length === 0}
                onChange={(event) => {
                  const next = event.target.value;
                  setChain([...chain.slice(0, depth - 1), ...(next ? [next] : [])]);
                }}
              >
                <option value="">
                  {options.length === 0 && depth > 1
                    ? t("articleSetup.chooseAbove", { level: levelLabel(depth - 1) })
                    : t("articleSetup.choose")}
                </option>
                {options.map((node) => (
                  <option key={node.category.id} value={node.category.id}>
                    {node.category.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ))}

        <Field label={t("articleSetup.brand")}>
          {(props) => (
            <Select
              {...props}
              value={brand}
              disabled={!mayWrite}
              onChange={(event) => setBrand(event.target.value)}
            >
              <option value="">
                {data.facts.brand
                  ? t("articleSetup.brandErp", { brand: data.facts.brand })
                  : t("articleSetup.noBrand")}
              </option>
              {brandOptions.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={t("articleSetup.imageUrl")} hint={t("articleSetup.imageUrlHint")}>
          {(props) => (
            <div className="hardauto-setup-image">
              <Input
                {...props}
                type="url"
                inputMode="url"
                value={image}
                disabled={!mayWrite}
                placeholder="https://"
                onChange={(event) => setImage(event.target.value)}
              />
              {imageShown ? (
                <img className="hardauto-setup-image__preview" src={image.trim()} alt="" />
              ) : null}
            </div>
          )}
        </Field>

        <Field
          label={t("articleSetup.group")}
          hint={
            inheritedGroup && !groupId
              ? t("articleSetup.groupInherited", {
                  path: data.attribute_group_inherited_from,
                })
              : undefined
          }
        >
          {(props) => (
            <Select
              {...props}
              value={groupId}
              disabled={!mayWrite}
              onChange={(event) => setGroupId(event.target.value)}
            >
              <option value="">
                {inheritedGroup
                  ? t("articleSetup.groupFromTree", { group: inheritedGroup.label })
                  : t("articleSetup.noGroup")}
              </option>
              {(groups.data?.items ?? []).map((group) => (
                <option key={group.id} value={group.id}>
                  {group.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <section
          className="hardauto-setup-grid__wide hardauto-setup-attributes"
          aria-label={
            chosenGroup
              ? t("articleSetup.attributesOf", { group: chosenGroup.label })
              : t("articleSetup.attributes")
          }
        >
          <header className="hardauto-setup-attributes__head">
            <h3 className="hardauto-setup-attributes__title">
              {chosenGroup
                ? t("articleSetup.attributesOf", { group: chosenGroup.label })
                : t("articleSetup.attributes")}
            </h3>
            <Badge tone={data.completion.publishable ? "success" : "warning"}>
              {t("articleSetup.completion", { score: data.completion.score })}
            </Badge>
          </header>
          {definitions.length === 0 ? (
            <p className="hardauto-cell__muted">
              {chosenGroup ? t("articleSetup.groupEmpty") : t("articleSetup.noAttributes")}
            </p>
          ) : (
            <div className="hardauto-setup-grid">
              {definitions.map((item) => (
                <AttributeInput
                  key={item.id}
                  definition={item}
                  value={values[item.key] ?? ""}
                  disabled={!mayWrite}
                  onChange={(next) => setValues({ ...values, [item.key]: next })}
                />
              ))}
            </div>
          )}
        </section>

        <Field label={t("articleSetup.webDesignation")}>
          {(props) => (
            <Input
              {...props}
              value={designation}
              disabled={!mayWrite}
              placeholder={data.facts.designation}
              onChange={(event) => setDesignation(event.target.value)}
            />
          )}
        </Field>

        {mayWrite ? (
          <div className="hardauto-setup-grid__wide hardauto-inline-form__actions">
            <Button type="submit" variant="primary" busy={action.isBusy("save")}>
              {t("articleSetup.save")}
            </Button>
          </div>
        ) : null}
      </form>
    </Card>
  );
}

/** One input, drawn from the attribute's declared type. */
function AttributeInput({
  definition,
  value,
  disabled,
  onChange,
}: {
  definition: AttributeDefinition;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const label = definition.unit ? `${definition.label} (${definition.unit})` : definition.label;

  if (definition.type === "boolean") {
    return (
      <Checkbox
        label={label}
        checked={value === "true"}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked ? "true" : "")}
      />
    );
  }

  return (
    <Field label={label} required={definition.required}>
      {(props) =>
        definition.type === "choice" ? (
          <Select
            {...props}
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
          >
            <option value="">{t("articleSetup.choose")}</option>
            {definition.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            {...props}
            value={value}
            inputMode={definition.type === "number" ? "decimal" : undefined}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
          />
        )
      }
    </Field>
  );
}
